import { useMemo } from "react";
import { parseDate, MANUFACTURING_STATUSES, QC_STATUSES, DISPATCH_STATUSES } from "./utils";
import { isItemBlocked, isItemOverdue, startOfToday } from "./stageFlags";

export function useStageStats({ items, stage, globalFilter, clients, clientGroups, dueThisWeekDays, statusKeys = null }) {
  const clientColorMap = useMemo(
    () => Object.fromEntries(clients.map(c => [c.name, c.color])),
    [clients]
  );

  const clientLogoMap = useMemo(() => {
    const map = {};
    clients.forEach(c => {
      if (c.logo_svg) { map[c.name] = c.logo_svg; return; }
      const group = (clientGroups || []).find(g => (g.client_ids || []).includes(c.id));
      if (group?.logo_svg) { map[c.name] = group.logo_svg; }
    });
    return map;
  }, [clients, clientGroups]);

  const partDuplicateMap = useMemo(() => {
    const map = new Map();
    for (const it of items) {
      const pn = (it.part_number || "").trim();
      if (!pn) continue;
      if (!map.has(pn)) map.set(pn, []);
      map.get(pn).push({ id: it.id, stage: it.stage, po_number: it.po_number, line_number: it.line_number, quantity: it.quantity });
    }
    for (const [key, arr] of map) {
      if (arr.length <= 1) map.delete(key);
    }
    return map;
  }, [items]);

  const dueThisWeekItems = useMemo(() => {
    const today = new Date(); today.setHours(0,0,0,0);
    const nextWeek = new Date(today); nextWeek.setDate(today.getDate() + dueThisWeekDays);
    return items.filter(i => {
      if (i.stage !== stage) return false;
      if (globalFilter.clients != null && !globalFilter.clients.includes(i.client)) return false;
      if (globalFilter.priority !== "all" && i.priority !== globalFilter.priority) return false;
      const d = parseDate(i.dispatch_due_date || i.due_date);
      return d && d >= today && d <= nextWeek;
    });
  }, [items, stage, globalFilter, dueThisWeekDays]);

  const reworkStageItems = useMemo(() => items.filter(i => {
    if (i.stage !== stage) return false;
    if (!i.is_rework) return false;
    if (globalFilter.clients != null && !globalFilter.clients.includes(i.client)) return false;
    if (globalFilter.priority !== "all" && i.priority !== globalFilter.priority) return false;
    return true;
  }), [items, stage, globalFilter]);

  const blockedStageItems = useMemo(() => items.filter(i => {
    if (i.stage !== stage) return false;
    if (!isItemBlocked(i)) return false;
    if (globalFilter.clients != null && !globalFilter.clients.includes(i.client)) return false;
    if (globalFilter.priority !== "all" && i.priority !== globalFilter.priority) return false;
    return true;
  }), [items, stage, globalFilter]);

  const qcPendingCount = useMemo(() => {
    if (stage !== "Quality Control") return 0;
    return items.filter(i => {
      if (i.stage !== "Quality Control") return false;
      if (globalFilter.clients != null && !globalFilter.clients.includes(i.client)) return false;
      if (globalFilter.priority !== "all" && i.priority !== globalFilter.priority) return false;
      return (i.qc_status || "Pending Inspection") === "Pending Inspection";
    }).length;
  }, [items, stage, globalFilter]);

  const stageScopedItems = useMemo(() => items.filter(i => {
    if (i.stage !== stage) return false;
    if (globalFilter.clients != null && !globalFilter.clients.includes(i.client)) return false;
    if (globalFilter.priority !== "all" && i.priority !== globalFilter.priority) return false;
    return true;
  }), [items, stage, globalFilter]);

  const overdueCount = useMemo(() => {
    const today = startOfToday();
    return stageScopedItems.filter(i => isItemOverdue(i, today)).length;
  }, [stageScopedItems]);

  const statusCounts = useMemo(() => {
    // Division lens (Phase 1b): `statusKeys` (from StagePage) is the base
    // chip list for the active lens — the historic metallic list in the All
    // lens (unchanged behaviour), the division's own list when scoped. Any
    // stray keys present on items (mixed All lens, legacy values) are added
    // with their counts so nothing ever disappears.
    const counts = {};
    const field = stage === "Manufacturing" ? "manufacturing_status"
                : stage === "Quality Control" ? "qc_status"
                : stage === "Dispatch" ? "dispatch_status" : null;
    if (!field) return counts;
    const dflt = stage === "Manufacturing" ? "Queued"
               : stage === "Quality Control" ? "Pending Inspection" : "Pending";
    const fallbackBase = stage === "Manufacturing" ? MANUFACTURING_STATUSES
                       : stage === "Quality Control" ? QC_STATUSES : DISPATCH_STATUSES;
    (statusKeys && statusKeys.length ? statusKeys : fallbackBase).forEach(s => { counts[s] = 0; });
    stageScopedItems.forEach(i => {
      const s = i[field] || dflt;
      counts[s] = (counts[s] || 0) + 1;
    });
    return counts;
  }, [stage, stageScopedItems, statusKeys]);

  return { clientColorMap, clientLogoMap, partDuplicateMap, dueThisWeekItems, reworkStageItems, blockedStageItems, qcPendingCount, stageScopedItems, overdueCount, statusCounts };
}
